import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { User, Building, Mail, Phone, MapPin, Lock, Upload } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ManageProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    adminName: '',
    adminEmail: user?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/profile`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      toast({
        title: 'Success',
        description: 'Profile updated successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast({
        title: 'Error',
        description: 'Password must be at least 8 characters',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      toast({
        title: 'Success',
        description: 'Password changed successfully'
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="manage-profile-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-brand-semibold text-brand mb-2">Profile Settings</h1>
        <p className="text-brand-text-secondary">Manage your company information and account settings</p>
      </div>

      {/* Change Password Card */}
      <Card className="card-brand">
        <CardHeader>
          <CardTitle className="flex items-center text-brand-primary">
            <Lock className="h-5 w-5 mr-2" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-brand-medium text-brand mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-brand-medium text-brand mb-2">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-brand-medium text-brand mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-brand-primary"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Company Information Card */}
      <Card className="card-brand">
        <CardHeader>
          <CardTitle className="flex items-center text-brand-primary">
            <Building className="h-5 w-5 mr-2" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-brand-medium text-brand mb-2">
                  <Building className="h-4 w-4 inline mr-1" />
                  Company Name
                </label>
                <input
                  type="text"
                  value={profileData.companyName}
                  onChange={(e) => setProfileData({ ...profileData, companyName: e.target.value })}
                  className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-brand-medium text-brand mb-2">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Company Email
                </label>
                <input
                  type="email"
                  value={profileData.companyEmail}
                  onChange={(e) => setProfileData({ ...profileData, companyEmail: e.target.value })}
                  className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  placeholder="company@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-brand-medium text-brand mb-2">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={profileData.companyPhone}
                  onChange={(e) => setProfileData({ ...profileData, companyPhone: e.target.value })}
                  className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  placeholder="+91 1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-brand-medium text-brand mb-2">
                  <User className="h-4 w-4 inline mr-1" />
                  Admin Name
                </label>
                <input
                  type="text"
                  value={profileData.adminName}
                  onChange={(e) => setProfileData({ ...profileData, adminName: e.target.value })}
                  className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  placeholder="John Doe"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-brand-medium text-brand mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                Company Address
              </label>
              <textarea
                value={profileData.companyAddress}
                onChange={(e) => setProfileData({ ...profileData, companyAddress: e.target.value })}
                className="w-full px-4 py-3 border border-brand rounded-brand focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                rows="3"
                placeholder="Enter company address"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-brand padding-brand">
              <p className="text-sm text-brand">💡 <strong>Tip:</strong> Keep your company information up to date for better collaboration and communication.</p>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-brand-primary"
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageProfile;
